import {
  DownloadOutlined,
  EmailOutlined,
  Info,
  InfoOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Button,
  CircularProgress,
  Icon,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Stack } from "@mui/system";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { COLOR_CONSTANTS } from "../../../helpers/Colors";
import {
  convertDate,
  convertToINR,
  formatDotDateTimeShort,
} from "../../../helpers/Utils";
import {
  ApprovalStatus,
  InvoiceStatus,
  MailingStatus,
  OrderStatus,
  POStatus,
  FinsStatus,
  VENDOR_ACTION_PO_STATUSES,
  _STATUS,
} from "../constants/statusMapping";
import OrderStatusBadge from "./OrderStatusBadge";
import { Checkbox } from "@mui/material";
import { getEffectivePoStatus, getPoDestination } from "../utils/poRouting";
import { getUTRString } from "../utils/payments";
import FormControlLabel from "@mui/material/FormControlLabel";
import OrderCard from "./OrderCard";
import CustomTooltip from "./CustomTooltip";

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: "#767676",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const tdStyle = {
  fontSize: "14px",
  py: "12px",
};

function TotalPaidCell({ row, getUTRString, sx }) {
  return (
    <TableCell align="right" sx={sx}>
      <Box display={"flex"} alignItems={"center"} justifyContent={"flex-end"}>
        {convertToINR(row.total_amount_paid)}
        <Box width={"5px"} />
        {row.payments && row.payments.length ? (
          <CustomTooltip
            title={
              <React.Fragment>
                {row.payments?.map((payment) => (
                  <div key={payment.id}>
                    {getUTRString(payment)}{" "}
                    {payment.payment_date
                      ? `[${convertDate(payment.payment_date)}]`
                      : ""}
                  </div>
                ))}
              </React.Fragment>
            }
            arrow
            placement={"bottom-start"}
          >
            <Icon>
              <InfoOutlined fontSize={"20"} />
            </Icon>
          </CustomTooltip>
        ) : null}
      </Box>
    </TableCell>
  );
}

// Component to show PO details and show/collapse the nested invoices
function OrderRow(props) {
  const {
    row,
    actionHandler,
    isActionLoading,
    selectInvoice,
    setBulkSelected,
    bulkSelected,
    showSelection,
    isAllOrders,
    activeFilterStatuses,
    hideInvoiceExpand,
  } = props;
  const effectivePoStatus = getEffectivePoStatus(row, activeFilterStatuses);
  const isSelected = bulkSelected.some(
    (item) => item.id === row.id && item.po_code === row.po_code,
  );
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const destination = getPoDestination(row, effectivePoStatus);

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{ ...(!hideInvoiceExpand && { "& > *": { borderBottom: "unset" } }), cursor: showSelection ? "default" : "pointer" }}
        onClick={!showSelection ? () => navigate(destination, { state: { from: "dashboard", poId: row.metadata?.po_id ?? row.po_id } }) : undefined}
      >
        {showSelection ? (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              // title={parser_invoice_po_result.match_status ? "Select" : "Not applicable"}
              checked={isSelected}
              // disabled={!parser_invoice_po_result?.match_status}
              onChange={(e) => {
                if (e.target.checked) {
                  setBulkSelected((prev) => [...prev, row]);
                } else {
                  setBulkSelected((prev) =>
                    prev.filter((item) => !(item.po_code === row.po_code)),
                  );
                }
              }}
            />
          </TableCell>
        ) : null}
        {!hideInvoiceExpand && (
          <TableCell sx={{ paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
            {row.invoices && row.invoices.length ? (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            ) : null}
          </TableCell>
        )}

        <TableCell sx={{ ...tdStyle, minWidth: 220, paddingLeft: 1 }} onClick={(e) => e.stopPropagation()}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            {showSelection ? (
              <Typography fontSize="14px">{row.po_code}</Typography>
            ) : (
              <Link
                to={destination}
                state={{
                  from: "dashboard",
                  poId: row.metadata?.po_id ?? row.po_id,
                }}
              >
                <Button
                  type="primary"
                  sx={{ color: COLOR_CONSTANTS["on-primary/variant"] }}
                >
                  {row.po_code}
                </Button>
              </Link>
            )}
            {row.invoice_status === "exception" ? (
              <CustomTooltip
                title={row.invoice_reject_reason || "Exception"}
                arrow
                placement={"bottom-start"}
              >
                <Info
                  fontSize="small"
                  sx={{ color: COLOR_CONSTANTS["tertiary-yellow/default"] }}
                />
              </CustomTooltip>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell sx={tdStyle} align="left">{convertToINR(row.order_value, 2)}</TableCell>
        <TableCell sx={tdStyle}>{row.vendor_name || "N/A"}</TableCell>
        <TableCell sx={{ ...tdStyle, whiteSpace: "nowrap" }}>
          <Typography fontSize="13px">
            <strong>PID:</strong> {row.project_id || "N/A"}
          </Typography>
          <Typography fontSize="13px">
            <strong>City:</strong> {row.project_city || "N/A"}
          </Typography>
          {row.customer_name?.trim() && (
            <Typography fontSize="13px">
              <strong>Customer:</strong> {row.customer_name}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={tdStyle}>{row.business_unit || "N/A"}</TableCell>
        {isAllOrders ? (
          <>
            <TableCell sx={tdStyle}>
              {(() => {
                const poStatusEntry = POStatus[effectivePoStatus];
                if (!poStatusEntry) return null;
                return (
                  <Stack
                    direction={"column"}
                    gap={"4px"}
                    alignItems={"flex-start"}
                  >
                    <OrderStatusBadge type={poStatusEntry.code} label={poStatusEntry.label} />
                    {row.status_change_time ? (
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        {formatDotDateTimeShort(row.status_change_time)}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              })()}
            </TableCell>
            <TableCell sx={tdStyle}>
              <Stack direction={"column"} gap={"4px"}>
                <OrderStatusBadge
                  type={InvoiceStatus[row.invoice_status]?.code}
                  label={InvoiceStatus[row.invoice_status]?.label}
                />
                <Typography color={"grey"} variant={"caption"}>
                  {row.invoice_uploaded ? "" : "Invoice copy missing"}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell sx={tdStyle} align="right">
              {convertToINR(row.total_invoice_value || 0)}
            </TableCell>
            <TotalPaidCell sx={tdStyle} row={row} getUTRString={getUTRString} />
            <TableCell sx={tdStyle}>{row.advance_required ? "Yes" : "No"}</TableCell>
          </>
        ) : (
          <>
            <TableCell sx={tdStyle}>
              <Box width={"100%"}>
                <OrderStatusBadge
                  type={
                    showSelection
                      ? FinsStatus[row.fins_status]?.code
                      : OrderStatus[row.order_status]?.code
                  }
                  label={
                    showSelection
                      ? FinsStatus[row.fins_status]?.label
                      : OrderStatus[row.order_status]?.label
                  }
                />
              </Box>
            </TableCell>
            <TableCell sx={tdStyle}>
              {MailingStatus[row.mail_sent]?.label || "N/A"}
            </TableCell>
            <TableCell sx={tdStyle}>{row.advance_required ? "Yes" : "No"}</TableCell>
            <TableCell sx={tdStyle}>
              <Stack direction={"column"} gap={"4px"}>
                <OrderStatusBadge
                  type={InvoiceStatus[row.invoice_status]?.code}
                  label={InvoiceStatus[row.invoice_status]?.label}
                />
                <Typography color={"grey"} variant={"caption"}>
                  {row.invoice_uploaded ? "" : "Invoice copy missing"}
                </Typography>
              </Stack>
            </TableCell>

            <TotalPaidCell sx={tdStyle} row={row} getUTRString={getUTRString} />
          </>
        )}
        <TableCell sx={tdStyle} align="right" onClick={(e) => e.stopPropagation()}>
          {isActionLoading[row.po_code] ? (
            <CircularProgress size={24} />
          ) : (
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"flex-end"}
              gap={1}
            >
              <CustomTooltip title={"View PO"} arrow>
                <IconButton
                  size="small"
                  onClick={() => actionHandler(row, "VIEW")}
                >
                  <VisibilityOutlined />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip title={"Download PO"} arrow>
                <IconButton
                  size="small"
                  onClick={() => actionHandler(row, "DOWNLOAD")}
                >
                  <DownloadOutlined />
                </IconButton>
              </CustomTooltip>
              {showSelection ? (
                <CustomTooltip title={"Resend mail to vendor"} arrow>
                  <IconButton
                    size="small"
                    onClick={() => actionHandler(row, "MAIL")}
                  >
                    <EmailOutlined />
                  </IconButton>
                </CustomTooltip>
              ) : null}
            </Stack>
          )}
        </TableCell>
      </TableRow>
      {!hideInvoiceExpand && <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ padding: 1 }} bgcolor={"#F2F2F2"}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Inv. number</TableCell>
                    <TableCell>VM validation</TableCell>
                    <TableCell>GRN ID</TableCell>
                    <TableCell>GRN Value</TableCell>
                    <TableCell>GRN Date</TableCell>
                    <TableCell>L1 validation</TableCell>
                    <TableCell>L2 Validation</TableCell>
                    <TableCell align="right">Invoice Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.invoices?.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.invoice_no}</TableCell>
                      <TableCell>
                        <OrderStatusBadge
                          type={
                            invoice.is_invoice_exception ||
                            invoice.additional_charges > 0
                              ? ApprovalStatus[invoice.vm_approval_status]?.code
                              : _STATUS.not_applicable
                          }
                          label={
                            invoice.vm_approval_status === null
                              ? "Not Applicable"
                              : invoice.vm_approval_status
                          }
                        />
                      </TableCell>
                      <TableCell>{invoice.grn_id || "N/A"}</TableCell>
                      <TableCell>{convertToINR(invoice.grn_value)}</TableCell>
                      <TableCell>{convertDate(invoice.grn_date)}</TableCell>
                      <TableCell>
                        <OrderStatusBadge
                          type={
                            invoice.l1_approval_status === "not_required"
                              ? _STATUS.not_applicable
                              : ApprovalStatus[invoice.l1_approval_status]?.code
                          }
                          label={
                            invoice.l1_approval_status === "not_required"
                              ? "Not Applicable"
                              : invoice.l1_approval_status
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge
                          type={
                            ApprovalStatus[invoice.l2_approval_status]?.code
                          }
                          label={invoice.l2_approval_status}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          gap={1}
                        >
                          {convertToINR(invoice.invoice_value)}
                          {invoice.utr ? (
                            <CustomTooltip title={invoice.utr}>
                              <InfoOutlined />
                            </CustomTooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>}
    </React.Fragment>
  );
}

export default function OrderTable({
  data,
  actionHandler,
  isActionLoading,
  bulkSelection,
  selectInvoice,
  orderStatus,
  activeFilterStatuses,
  hideInvoiceExpand,
  isMobile = false,
}) {
  const [bulkSelected, setBulkSelected] = bulkSelection;
  const showSelection =
    VENDOR_ACTION_PO_STATUSES.has(orderStatus) &&
    orderStatus !== "revision_requested";
  const isAllOrders = orderStatus === "all_orders";
  if (isMobile) {
    return (
      <Stack spacing={0.75} sx={{ p: 1 }}>
        {showSelection ? (
          <FormControlLabel
            sx={{ ml: 0 }}
            label={<Typography fontSize="14px">Select all</Typography>}
            control={
              <Checkbox
                size="small"
                checked={bulkSelected.length === (data?.length ?? 0)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setBulkSelected(data);
                  } else {
                    setBulkSelected([]);
                  }
                }}
              />
            }
          />
        ) : null}
        {(data || []).map((row, index) => (
          <OrderCard
            key={`${row.po_code}_${index}_${row.id}_${row.order_status}`}
            row={row}
            actionHandler={actionHandler}
            isActionLoading={isActionLoading}
            setBulkSelected={setBulkSelected}
            bulkSelected={bulkSelected}
            showSelection={showSelection}
            isAllOrders={isAllOrders}
            activeFilterStatuses={activeFilterStatuses}
          />
        ))}
      </Stack>
    );
  }
  return (
    <TableContainer sx={{ overflow: "visible" }}>
      <Table aria-label="collapsible table" stickyHeader>
        <TableHead>
          <TableRow sx={{ "& th": { borderBottomWidth: 2, borderColor: "#ccc" } }}>
            {showSelection ? (
              <TableCell>
                <Checkbox
                  size="small"
                  checked={bulkSelected.length === (data?.length ?? 0)}
                  // disabled={
                  //   data.filter(
                  //     (row) =>
                  //       row.validation_result?.parser_invoice_po_result
                  //         ?.match_status
                  //   )?.length === 0
                  // }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setBulkSelected(data);
                    } else {
                      setBulkSelected([]);
                    }
                  }}
                />
              </TableCell>
            ) : null}
            {!hideInvoiceExpand && <TableCell />}

            <TableCell sx={thStyle}>Order Code</TableCell>
            <TableCell sx={thStyle} align="right">Order Value</TableCell>
            <TableCell sx={thStyle}>Vendor Name</TableCell>
            <TableCell sx={thStyle}>Project</TableCell>
            <TableCell sx={thStyle}>Business Unit</TableCell>
            {isAllOrders ? (
              <>
                <TableCell sx={thStyle}>PO Status</TableCell>
                <TableCell sx={thStyle}>Invoice Status</TableCell>
                <TableCell sx={thStyle} align="right">Total Invoice Amount</TableCell>
                <TableCell sx={thStyle} align="right">Total Amount Paid</TableCell>
                <TableCell sx={thStyle}>Advance Required</TableCell>
              </>
            ) : (
              <>
                <TableCell sx={thStyle}>Order Status</TableCell>
                <TableCell sx={thStyle}>Mailing Status</TableCell>
                <TableCell sx={thStyle}>Advance Required</TableCell>
                <TableCell sx={thStyle}>Invoice Status</TableCell>
                <TableCell sx={thStyle} align="right">Total Amount Paid</TableCell>
              </>
            )}
            <TableCell sx={thStyle}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Order details tab  */}
          {(data || []).map((row, index) => (
            <OrderRow
              key={`${row.po_code}_${index}_${row.id}_${row.order_status}`}
              row={row}
              actionHandler={actionHandler}
              isActionLoading={isActionLoading}
              selectInvoice={selectInvoice}
              setBulkSelected={setBulkSelected}
              bulkSelected={bulkSelected}
              showSelection={showSelection}
              isAllOrders={isAllOrders}
              activeFilterStatuses={activeFilterStatuses}
              hideInvoiceExpand={hideInvoiceExpand}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
